import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Formik,
  FormikCoherenceQuestion,
  FormikInsurance,
  InputSelectOption,
  Question,
  QUESTIONNAIRE_CODE,
  YesNoAnswersNew,
} from "src/types";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  atomDossier,
  atomPolicy,
  atomQuestionnaire,
  atomShowQuestionnaireFailure,
} from "src/storage";
import { useResponsiveBp } from "src/utils/hook-functions/responsive-bp.hook";
import { useGetQuestionnaire } from "src/networking/terminals";

export const useDataEntryStep3 = (formik: Formik<FormikInsurance>) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsiveBp();

  const {
    values: { coherenceQuestions },
    setFieldValue,
  } = formik;

  const dossier = useRecoilValue(atomDossier);
  const policy = useRecoilValue(atomPolicy);
  const [questionnaire, saveQuestionnaire] = useRecoilState(atomQuestionnaire);
  const { crossGate: getQuestionnaire, pending: getQuestionnairePending } =
    useGetQuestionnaire();

  const showQuestionnaireFailure = useRecoilValue(atomShowQuestionnaireFailure);

  useEffect(() => {
    if (!dossier || questionnaire) return;

    setFieldValue("coherenceQuestions", []);

    getQuestionnaire(
      {
        headers: {
          xDossierId: dossier?.dossierId,
        },
        uriParams: {
          idPolizza: policy?.idPolizza || "",
          codQuestionario: QUESTIONNAIRE_CODE,
        },
      },
      {
        onSuccess: (questionnaire) => {
          saveQuestionnaire(questionnaire);
          const formikQuestions: FormikCoherenceQuestion[] =
            questionnaire.questionario.domande.reduce<
              FormikCoherenceQuestion[]
            >((acc, domanda) => {
              if (domanda.attiva) acc.push(createFormikQuestion(domanda));
              return acc;
            }, []);

          setFieldValue("coherenceQuestions", formikQuestions);
        },
      }
    );
  }, []);

  useEffect(() => {
    if (!showQuestionnaireFailure || isDesktop) return;

    document?.getElementById("unsuitable-modal")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [showQuestionnaireFailure]);

  const createFormikQuestion = (
    question: Question
  ): FormikCoherenceQuestion => {
    const idyes =
      question.risposte
        .find((rispsota) => rispsota.valore.toUpperCase().trim() === "SI")
        ?.idRisposta.toString() || YesNoAnswersNew.YES;

    const idNo =
      question.risposte
        .find((rispsota) => rispsota.valore.toUpperCase().trim() === "NO")
        ?.idRisposta.toString() || YesNoAnswersNew.NO;

    const coherenceQuestions: InputSelectOption<string>[] = [
      { id: idyes, value: t("commons.yes_label") },
      { id: idNo, value: t("commons.no_label") },
    ];

    return {
      id: question.idDomanda,
      label: question.descrizione,
      options: coherenceQuestions,
    };
  };

  const continueDisabled = useMemo(() => {
    return coherenceQuestions.some((question) => !question.answer);
  }, [coherenceQuestions]);

  return {
    isDesktop,
    showQuestionnaireFailure,
    continueDisabled,
    getQuestionnairePending,
    questionnaire,
    t,
  };
};
